import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getSystemsData, GetSystemsDataOutputType } from 'zite-endpoints-sdk';
import { ExternalLink, CheckCircle2, Circle, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

type AttensiCourse = GetSystemsDataOutputType['attensiCourses'][0];

export default function Systems() {
  const { user } = useAuth();
  const [data, setData] = useState<GetSystemsDataOutputType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getSystemsData({}).then(setData).finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6">
          <h1 className="text-xl font-bold text-foreground">Systems & Tools</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Access your payroll, shifts, training and holiday tools below.
          </p>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-2xl space-y-4">

        {/* Xero Me */}
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">X</div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Xero Me</h3>
              <p className="text-xs text-muted-foreground">Payroll & HR</p>
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1.5 mb-4">
            <li>• View and download your <strong className="text-foreground">payslips</strong></li>
            <li>• Submit <strong className="text-foreground">holiday requests</strong> to your manager</li>
            <li>• View your employment details and tax info</li>
          </ul>
          {data?.xeroId ? (
            <p className="text-xs text-muted-foreground mb-3">Your Xero ID: <span className="text-foreground font-mono">{data.xeroId}</span></p>
          ) : (
            <p className="text-xs text-amber-500 mb-3">Not enrolled — speak to your manager to get access</p>
          )}
          <a
            href="https://login.xero.com/identity/user/login"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            Open Xero Me <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Planday */}
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">P</div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Planday</h3>
              <p className="text-xs text-muted-foreground">Scheduling & Shifts</p>
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1.5 mb-4">
            <li>• View your <strong className="text-foreground">upcoming shifts and rota</strong></li>
            <li>• Request <strong className="text-foreground">shift swaps</strong> with colleagues</li>
            <li>• Set your <strong className="text-foreground">availability</strong> and time-off preferences</li>
          </ul>
          {data?.plandayId ? (
            <p className="text-xs text-muted-foreground mb-3">Your Planday ID: <span className="text-foreground font-mono">{data.plandayId}</span></p>
          ) : (
            <p className="text-xs text-amber-500 mb-3">Not enrolled — speak to your manager to get access</p>
          )}
          <a
            href="https://app.planday.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            Open Planday <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Attensi */}
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">A</div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Attensi</h3>
              <p className="text-xs text-muted-foreground">General Training</p>
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1.5 mb-4">
            <li>• Complete your <strong className="text-foreground">required training courses</strong></li>
            <li>• Access additional development modules</li>
          </ul>

          {/* Required Courses */}
          {loading ? (
            <div className="space-y-2 mb-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : data && data.attensiCourses.length > 0 ? (
            <div className="border border-border rounded-md p-3 mb-4">
              <p className="text-xs font-medium text-foreground mb-2">Your Required Courses</p>
              <div className="space-y-2">
                {data.attensiCourses.map((course: AttensiCourse) => (
                  <div key={course.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      {course.status === 'Completed'
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                        : <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      }
                      <span className={course.status === 'Completed' ? 'text-muted-foreground line-through' : 'text-foreground'}>
                        {course.courseName}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-sm text-xs ${course.status === 'Completed' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {course.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : !loading ? (
            <p className="text-xs text-muted-foreground mb-4 italic">No courses assigned yet — speak to your manager.</p>
          ) : null}

          <a
            href="https://app.attensi.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            Open Attensi <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Info note */}
        <div className="bg-muted/40 border border-border rounded-lg px-5 py-4 flex items-start gap-3 text-sm text-muted-foreground">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <div>
            <strong className="text-foreground">Not enrolled yet?</strong> Ask your manager to set you up on Xero, Planday, and Attensi. Your IDs will appear here once active.
          </div>
        </div>
      </div>
    </div>
  );
}
